import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axios from "axios";

const defaultDraft = {
  id: null,
  countryCode: "",
  stateCode: "",
  type: "",
  ratePercent: "",
};

const toPercentString = (rate) => {
  if (rate === null || rate === undefined || Number.isNaN(Number(rate))) return "";
  return (Number(rate) * 100).toFixed(2);
};

const buildLabel = (tax) => {
  const country = tax.countryCode ? tax.countryCode.toUpperCase() : "";
  const state = tax.stateCode ? tax.stateCode.toUpperCase() : "";
  const type = tax.type ? tax.type.toUpperCase() : "";
  const rate = toPercentString(tax.rate);
  const parts = [country, state].filter(Boolean).join(" / ");
  return `${parts} ${type ? `(${type})` : ""}`.trim() + (rate ? ` — ${rate}%` : "");
};

export default function TaxCodesTab({ instanceUniqueId }) {
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(defaultDraft);
  const [instanceId, setInstanceId] = useState(null);

  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const fetchInstanceId = async () => {
    if (!instanceUniqueId) return;
    const res = await axios.get(
      `${SERVER}/instances/byinstanceUniqueId/${instanceUniqueId}`,
      headers
    );
    setInstanceId(res.data?.id || null);
  };

  const fetchTaxes = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${SERVER}/instances/${targetInstanceId}/taxes`,
        headers
      );
      setTaxes(res.data.taxes || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstanceId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  useEffect(() => {
    if (instanceId) fetchTaxes(instanceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  const openCreate = () => {
    setDraft(defaultDraft);
    setOpen(true);
  };

  const openEdit = (tax) => {
    setDraft({
      id: tax.taxId,
      countryCode: tax.countryCode || "",
      stateCode: tax.stateCode || "",
      type: tax.type || "",
      ratePercent: toPercentString(tax.rate),
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      countryCode: draft.countryCode.trim(),
      stateCode: draft.stateCode.trim(),
      type: draft.type.trim(),
      rate: Number(draft.ratePercent) / 100,
    };

    if (
      !payload.countryCode ||
      !payload.stateCode ||
      !payload.type ||
      Number.isNaN(payload.rate) ||
      !instanceId
    ) {
      return;
    }

    if (draft.id) {
      await axios.put(
        `${SERVER}/instances/${instanceId}/taxes/${draft.id}`,
        payload,
        headers
      );
    } else {
      await axios.post(`${SERVER}/instances/${instanceId}/taxes`, payload, headers);
    }

    setOpen(false);
    fetchTaxes(instanceId);
  };

  const remove = async (tax) => {
    if (!instanceId) return;
    await axios.delete(`${SERVER}/instances/${instanceId}/taxes/${tax.taxId}`, headers);
    fetchTaxes(instanceId);
  };

  const isValid = useMemo(() => {
    if (!draft.countryCode.trim() || !draft.stateCode.trim() || !draft.type.trim()) return false;
    const rate = Number(draft.ratePercent);
    return Number.isFinite(rate) && rate >= 0;
  }, [draft]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Tax Codes
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Configure tax codes for this instance.
          </Typography>
        </Box>

        <Button variant="contained" onClick={openCreate}>
          Add Tax Code
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Label</TableCell>
            <TableCell>Country</TableCell>
            <TableCell>State</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Rate</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(taxes || []).map((tax) => (
            <TableRow key={tax.taxId} hover>
              <TableCell sx={{ fontWeight: 700 }}>{buildLabel(tax)}</TableCell>
              <TableCell>{tax.countryCode?.toUpperCase() || "—"}</TableCell>
              <TableCell>{tax.stateCode?.toUpperCase() || "—"}</TableCell>
              <TableCell>{tax.type || "—"}</TableCell>
              <TableCell>{toPercentString(tax.rate)}%</TableCell>
              <TableCell align="right">
                <IconButton onClick={() => openEdit(tax)} size="small">
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton onClick={() => remove(tax)} size="small">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {!loading && (taxes || []).length === 0 && (
            <TableRow>
              <TableCell colSpan={6} sx={{ py: 4, textAlign: "center", opacity: 0.7 }}>
                No tax codes yet. Add your first one.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {draft.id ? "Edit Tax Code" : "Create Tax Code"}
        </DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 1 }}>
          <TextField
            label="Country Code"
            value={draft.countryCode}
            onChange={(e) => setDraft((prev) => ({ ...prev, countryCode: e.target.value }))}
            placeholder="US"
          />
          <TextField
            label="State/Region Code"
            value={draft.stateCode}
            onChange={(e) => setDraft((prev) => ({ ...prev, stateCode: e.target.value }))}
            placeholder="CA"
          />
          <TextField
            label="Type"
            value={draft.type}
            onChange={(e) => setDraft((prev) => ({ ...prev, type: e.target.value }))}
            placeholder="Sales"
          />
          <TextField
            label="Rate (%)"
            type="number"
            value={draft.ratePercent}
            onChange={(e) => setDraft((prev) => ({ ...prev, ratePercent: e.target.value }))}
            placeholder="7.25"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!isValid}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
